import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Card,
  Checkbox,
  Col,
  Empty,
  Form,
  Input,
  InputNumber,
  Layout,
  Menu,
  message,
  MenuProps,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Typography,
  Tag,
  Flex,
  DatePicker,
  Tooltip,
  Button
} from "antd";
import { InfoCircleOutlined, LockOutlined, PlusOutlined } from "@ant-design/icons";
import {
  entityNameMaxLengthRule,
  entityNameNoSpecialCharsRule,
} from "app/shared/validation/entityNameCharacters";
import {
  SchoolFormValues,
  clearSelectedSchool,
  loadCentres,
  loadCountries,
  selectCentreAndLoad,
  selectSchoolForEdit,
  submitSchoolForm,
  normalizeSchoolId,
  normalizeCentreId,
  findCentreBySelectedId,
} from "./actions";
import { getCentreById } from "app/shared/api/getCentreById";
import {
  selectCentres,
  selectCentresLoading,
  selectCountries,
  selectCountriesLoading,
  selectDetailError,
  selectDetailLoadingId,
  selectFormMode,
  selectSaveError,
  selectSaving,
  selectSchools,
  selectSchoolsLoading,
  selectSelectedCentreId,
  selectSelectedSchoolDetail,
  selectSelectedSchoolId,
} from "./selectors";
import "./SchoolManager.css";
import { Centre, Country, School, BookingSystem } from "app/shared/model";
import { getAllBookingSystems, updateBookingSystemSchoolExclusions } from "app/shared/api";
import DateHelper from "app/helpers/dateHelper";
import dayjs, { Dayjs } from "dayjs";
import UnsavedChangesModal from 'app/shared/ui/UnsavedChangesModal';
import { PublishedEventType, StatusIcon, useSignalREvent } from "app/helpers/signalRHelper";
import {
  focusFirstInvalidField,
  getFirstInvalidFieldName,
} from "app/shared/validation/focusFirstInvalidField";

const { Sider, Content } = Layout;
const { Title, Text } = Typography;

const phoneRule = {
  pattern: /^[+]?[\d\s().-]{7,20}$/,
  message: "Enter a valid telephone number",
};

const schoolPhoneRules = [
  phoneRule,
  { max: 20, message: "Telephone number must be 20 characters or fewer." },
];


const mapExcludedIdsToCheckboxValues = (
  excludedIds: (string | number)[] | undefined,
  bookingSystems: BookingSystem[]
): string[] => {
  if (!Array.isArray(excludedIds) || excludedIds.length === 0) return [];
  return excludedIds.map((id) => {
    const idStr = String(id).trim();
    const idNum = Number(idStr);
    const match = bookingSystems.find(
      (bs) =>
        String(bs.id) === idStr ||
        String(bs.id) === String(id) ||
        (Number(bs.id) === idNum && !Number.isNaN(idNum))
    );
    return match ? String(match.id) : idStr;
  });
};

const mapSchoolToFormValues = (school: School | undefined, bookingSystems: BookingSystem[] = []): Partial<SchoolFormValues> => {
  if (!school) {
    return {};
  }
  const rawExcluded = Array.isArray(school.excludedBookingSystemIds)
    ? school.excludedBookingSystemIds.map((id) => String(id))
    : [];
  const excludedBookingSystemIds =
    bookingSystems.length > 0
      ? mapExcludedIdsToCheckboxValues(school.excludedBookingSystemIds, bookingSystems)
      : rawExcluded;

  return {
    id: school.id,
    schoolName: school.name ?? "",
    schoolCode: school.code ?? "",
    telephoneNo: school.telephoneNo ?? school.telephone ?? "",
    emergencyNo: school.emergencyNo ?? school.emergencyTelephone ?? "",
    ageFrom: school.ageFrom ?? school.minimumAge ?? undefined,
    ageTo: school.ageTo ?? null,
    lmsAccess: school.hasLmsAccess ?? false,
    opensOnSaturday: school.opensOnSaturday ?? false,
    opensOnSunday: school.opensOnSunday ?? false,
    isActive: school.isActive ?? true,
    addressBuilding: school.address?.building ?? school.address?.line1 ?? "",
    addressStreet: school.address?.street ?? school.address?.line2 ?? "",
    addressCity: school.address?.city ?? school.address?.line3 ?? "",
    addressPostalCode: school.address?.postalCode ?? school.address?.line4 ?? "",
    addressCountry: school.address?.countryId != null
      ? String(school.address.countryId)
      : undefined,
    excludedBookingSystemIds,
    decommissionDate: school.decommissionDate ? (dayjs.isDayjs(school.decommissionDate) ? school.decommissionDate : DateHelper.ToDayJS(school.decommissionDate as any)) : null,
    lastActiveSchoolInCentre: school.lastActiveSchoolInCentre,
    entityUpdate: school.entityUpdate
  };
};

const sortCountries = (countries: Country[]): Country[] => {
  return [...countries].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
};

const deriveCentreKey = (centre: Centre): string =>
  String(
    centre.code ??
      (centre as any)?.centreCode ??
      (centre as any)?.id ??
      centre.name ??
      ""
  );

const EXCLUDED_COUNTRIES = new Set<string>(['Australia', 'New Zealand']);
const EXCLUDED_CENTRES = new Set<string>(['Washington', 'Miami', 'Oxford']);

const resolveCountryIdFromCentre = (centre: Centre | undefined): string | undefined => {
  if (!centre) return undefined;

  const countryId = (centre as any)?.centreAddress?.countryId ?? centre.countryId;
  return countryId != null && String(countryId).trim() !== ""
      ? String(countryId)
      : undefined;
};

const prefillFromCentre = (centre: Centre | undefined): Partial<SchoolFormValues> => {
  if (!centre) return {};

  const address: any = (centre as any).centreAddress;

  return {
    schoolName: centre.name ?? "",
    telephoneNo: (centre as any).telephoneNo ?? (centre as any).telephone ?? undefined,
    emergencyNo: (centre as any).emergencyNo ?? (centre as any).emergencyTelephone ?? undefined,

    addressBuilding: address?.line1 ?? "",
    addressStreet: address?.line2 ?? "",
    addressPostalCode: address?.line3 ?? "",
    addressCity: address?.line4 ?? "",

    addressCountry: resolveCountryIdFromCentre(centre),
  };
};

const prefillFromSchoolsList = (
  schools: School[],
  centre: Centre | undefined
): Partial<SchoolFormValues> => {
  const first = schools?.[0];
  const fromCentre = prefillFromCentre(centre);

  return {
    schoolName: first?.name ?? fromCentre.schoolName ?? "",
    telephoneNo: fromCentre.telephoneNo,
    emergencyNo: first?.emergencyNo ?? fromCentre.emergencyNo,
    addressBuilding: fromCentre.addressBuilding,
    addressStreet: fromCentre.addressStreet,
    addressPostalCode: fromCentre.addressPostalCode,
    addressCity: fromCentre.addressCity,
    addressCountry: fromCentre.addressCountry,
  };
};

const defaultFormValues: Partial<SchoolFormValues> = {
  lmsAccess: false,
  opensOnSaturday: false,
  opensOnSunday: false,
  isActive: true,
  excludedBookingSystemIds: []
};

type CountryGroup = {
  key: string;
  label: string;
  centres: Centre[];
  originalCount: number;
};

const Page: React.FC = () => {
  const dispatch = useDispatch<any>();
  const [form] = Form.useForm<SchoolFormValues>();
  const [initialised, setInitialised] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [centreSearch, setCentreSearch] = useState("");
  const [activeCountryKey, setActiveCountryKey] = useState<string | null>(null);
  const [openCountryKeys, setOpenCountryKeys] = useState<string[]>([]);
  const [bookingSystems, setBookingSystems] = useState<BookingSystem[]>([]);
  const [bookingSystemsLoading, setBookingSystemsLoading] = useState(false);
  const [initialExcludedBookingSystems, setInitialExcludedBookingSystems] = useState<string[]>([]);
  const [decommissionDate, setDecommissionDate] = useState<Dayjs | null>(null);
  const [pendingAction, setPendingAction] = useState<'close-modal' | null>(null);
  const [initialFormValues, setInitialFormValues] = useState<any>(null);
  const [initialDecommissionDate, setInitialDecommissionDate] = useState<Dayjs | null>(null);
  const lastOpenedSchoolIdRef = React.useRef<string | number | null>(null);
  const [currentCentre, setCurrentCentre] = useState<string>(() => { return localStorage.getItem("currentCentre") ?? ""; });
  const schoolIdRef = React.useRef<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  const centres = useSelector(selectCentres);
  const centresLoading = useSelector(selectCentresLoading);
  const selectedCentreId = useSelector(selectSelectedCentreId);

  const schools = useSelector(selectSchools);
  const schoolsLoading = useSelector(selectSchoolsLoading);
  const selectedSchoolId = useSelector(selectSelectedSchoolId);
  const selectedSchoolDetail = useSelector(selectSelectedSchoolDetail);
  const detailLoadingId = useSelector(selectDetailLoadingId);
  const detailError = useSelector(selectDetailError);

  const countries = useSelector(selectCountries);
  const countriesLoading = useSelector(selectCountriesLoading);

  const saving = useSelector(selectSaving);
  const saveError = useSelector(selectSaveError);
  const formMode = useSelector(selectFormMode);
  const countryNameMap = useMemo(() => {
    const map = new Map<string, string>();
    countries.forEach((country) => {
      const key = country?.id != null ? String(country.id).toUpperCase() : "";
      if (key) {
        map.set(key, country.name ?? key);
      }
    });
    return map;
  }, [countries]);

  const refreshGridEvent = async () => {
    dispatch(selectCentreAndLoad(currentCentre) as any);
  }

  // Subscribe to event and run refreshGridEvent when triggered
  const { status, lastEventReceived } = useSignalREvent([PublishedEventType.SchoolEventPublished], refreshGridEvent);

  useEffect(() => {
    let cancelled = false;
    setBookingSystemsLoading(true);
    getAllBookingSystems()
      .then((systems) => {
        if (!cancelled) {
          setBookingSystems(systems || []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setBookingSystems([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setBookingSystemsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!initialised) {
      dispatch(loadCentres() as any);
      dispatch(loadCountries() as any);
      setInitialised(true);
    }
  }, [dispatch, initialised]);

  const normalizeFormValue = (value: any): any => {
    if (value === null || value === undefined) {
      return undefined;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed === '' ? undefined : trimmed;
    }
    if (typeof value === 'boolean' || typeof value === 'number') {
      return value;
    }
    if (Array.isArray(value)) {
      if (value.length === 0) return undefined;
      const normalized = value.map(normalizeFormValue).filter((v) => v !== undefined);
      return normalized.length === 0 ? undefined : normalized;
    }
    if (typeof value === 'object') {
      const normalized: any = {};
      let hasValues = false;
      for (const key in value) {
        const normalizedVal = normalizeFormValue(value[key]);
        if (normalizedVal !== undefined) {
          normalized[key] = normalizedVal;
          hasValues = true;
        }
      }
      return hasValues ? normalized : undefined;
    }
    return value;
  };

  const normalizeFormValues = (values: any): any => {
    if (!values || typeof values !== 'object') return {};
    const normalized: any = {};
    for (const key in values) {
      const normalizedVal = normalizeFormValue(values[key]);
      if (normalizedVal !== undefined) {
        normalized[key] = normalizedVal;
      }
    }
    return normalized;
  };

  const checkFormDirty = useCallback(() => {
    if (!isModalOpen || !initialFormValues) return false;
    
    const currentValues = form.getFieldsValue(true);
    
    const currentDecommissionDate = decommissionDate;
    const initialDecommissionDateValue = initialDecommissionDate;
    if (currentDecommissionDate !== initialDecommissionDateValue) {
      if ((currentDecommissionDate == null && initialDecommissionDateValue == null)) {
      } else {
        return true;
      }
    }
    
    const normalizedCurrent = normalizeFormValues(currentValues);
    const normalizedInitial = normalizeFormValues(initialFormValues);
    
    delete normalizedCurrent.id;
    delete normalizedCurrent.decommissionDate;
    delete normalizedInitial.id;
    delete normalizedInitial.decommissionDate;
    
    const deepEqual = (a: any, b: any): boolean => {
      if (a === b) return true;
      if (a == null && b == null) return true;
      if (a == null || b == null) return false;
      if (typeof a !== 'object' || typeof b !== 'object') {
        return a === b;
      }
      
      if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) return false;
        return a.every((val, idx) => deepEqual(val, b[idx]));
      }
      
      const keysA = Object.keys(a);
      const keysB = Object.keys(b);
      if (keysA.length !== keysB.length) return false;
      
      for (const key of keysA) {
        if (!keysB.includes(key)) return false;
        if (!deepEqual(a[key], b[key])) return false;
      }
      return true;
    };
    
    return !deepEqual(normalizedCurrent, normalizedInitial);
  }, [form, isModalOpen, initialFormValues, decommissionDate, initialDecommissionDate]);

  useEffect(() => {
    if (formMode !== "create" || !isModalOpen) return;

    let cancelled = false;

    const applyCreatePrefill = async () => {
      form.resetFields();
      const baseValues = { ...defaultFormValues, excludedBookingSystemIds: [] };

      let currentSelectedCentre =
          selectedCentreId != null && selectedCentreId !== ""
              ? findCentreBySelectedId(centres, selectedCentreId)
              : undefined;

      if (currentSelectedCentre && !currentSelectedCentre.telephoneNo && !currentSelectedCentre.emergencyNo) {
        const centreIdRaw =
          (currentSelectedCentre as any)?.id ??
          (currentSelectedCentre as any)?.centreId ??
          selectedCentreId;
        const centreId = normalizeCentreId(centreIdRaw);
        const numericId = centreId ? Number(centreId) : NaN;

        if (Number.isFinite(numericId) && numericId > 0) {
          try {
            const details = await getCentreById(numericId);
            if (!cancelled && details) {
              currentSelectedCentre = {
                ...currentSelectedCentre,
                telephoneNo: details.telephone ?? currentSelectedCentre.telephoneNo,
                emergencyNo: details.emergencyTelephone ?? currentSelectedCentre.emergencyNo,
              };
            }
          } catch {

          }
        }
      }

      if (cancelled) return;

      const prefilledValues: Partial<SchoolFormValues> = currentSelectedCentre
        ? { ...baseValues, ...prefillFromSchoolsList(schools, currentSelectedCentre) }
        : { ...baseValues };

      form.setFieldsValue(prefilledValues);
      setInitialExcludedBookingSystems([]);
      setDecommissionDate(null);
      setInitialDecommissionDate(null);
      setTimeout(() => {
        const currentValues = form.getFieldsValue(true);
        const { decommissionDate: _, ...valuesWithoutDate } = currentValues;
        setInitialFormValues(JSON.parse(JSON.stringify(valuesWithoutDate)));
      }, 0);
    };

    void applyCreatePrefill();

    return () => {
      cancelled = true;
    };
  }, [formMode, form, selectedCentreId, centres, schools, isModalOpen]);

  useEffect(() => {
    if (!isModalOpen) return;
    if (formMode !== "edit" || !selectedSchoolDetail) return;

    const id = normalizeSchoolId(selectedSchoolDetail.id ?? null);
    if (!id) return;

    if (schoolIdRef.current === id) return;
    if (form.isFieldsTouched(true)) return;

    schoolIdRef.current = id;

    form.resetFields();
    const formValues = mapSchoolToFormValues(selectedSchoolDetail, bookingSystems);
    form.setFieldsValue(formValues);

    setDecommissionDate(selectedSchoolDetail.decommissionDate);
    setInitialDecommissionDate(selectedSchoolDetail.decommissionDate);
    setInitialExcludedBookingSystems(
        Array.isArray(selectedSchoolDetail.excludedBookingSystemIds)
            ? selectedSchoolDetail.excludedBookingSystemIds.map((x) => String(x))
            : []
    );

    setTimeout(() => {
      const currentValues = form.getFieldsValue(true);
      const { decommissionDate: _, ...valuesWithoutDate } = currentValues;
      setInitialFormValues(JSON.parse(JSON.stringify(valuesWithoutDate)));
    }, 0);
  }, [formMode, selectedSchoolDetail, isModalOpen, form, bookingSystems]);

  useEffect(() => { 
    localStorage.setItem("currentCentre", currentCentre); }, [currentCentre]
  );

  const groupedCentres = useMemo<CountryGroup[]>(() => {
    const groups = new Map<string, { label: string; centres: Centre[] }>();

    centres.forEach((centre) => {
      const rawCode = centre?.countryCode ? String(centre.countryCode).toUpperCase() : "OTHER";
      const knownLabel = centre?.countryName ?? countryNameMap.get(rawCode);
      const label = knownLabel && knownLabel.trim().length > 0
        ? knownLabel
        : rawCode === "OTHER"
        ? "Other"
        : rawCode || "Other";
      const key = rawCode || label || "OTHER";

      if (EXCLUDED_COUNTRIES.has(label)) {
        return;
      }

      if (
        EXCLUDED_CENTRES.has(
          (centre.name ?? centre.code ?? "").toString().trim()
        )
      ) {
        return;
      }

      if (!groups.has(key)) {
        groups.set(key, { label, centres: [] });
      }
      groups.get(key)!.centres.push(centre);
    });

    return Array.from(groups.entries())
      .map(([key, value]) => {
        const sortedCentres = [...value.centres].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        return {
          key,
          label: value.label,
          centres: sortedCentres,
          originalCount: sortedCentres.length,
        };
      })
      .filter((group) => group.centres.length > 0)
      .sort((a, b) => (a.label || "").localeCompare(b.label || ""));
  }, [centres, countryNameMap]);

  const displayGroupedCentres = useMemo<CountryGroup[]>(() => {
    if (showInactive) return groupedCentres;
    return groupedCentres
      .map((country) => ({
        ...country,
        centres: country.centres.filter((c) => c.active !== false),
        originalCount: country.originalCount,
      }))
      .filter((group) => group.centres.length > 0);
  }, [groupedCentres, showInactive]);

  const filteredCountries = useMemo<CountryGroup[]>(() => {
    const term = centreSearch.trim().toLowerCase();
    if (!term) {
      return displayGroupedCentres;
    }

    return displayGroupedCentres
      .map((country) => {
        const matchesCountry = country.label.toLowerCase().includes(term);
        if (matchesCountry) {
          return country;
        }

        const matchingCentres = country.centres.filter((centre) => {
          const name = (centre.name ?? "").toLowerCase();
          const code = deriveCentreKey(centre).toLowerCase();
          return name.includes(term) || code.includes(term);
        });

        if (matchingCentres.length > 0) {
          return {
            ...country,
            centres: matchingCentres,
          };
        }

      return null;
    })
    .filter((country): country is CountryGroup => country !== null);
  }, [displayGroupedCentres, centreSearch]);

  const centreToCountryKey = useMemo(() => {
    const map = new Map<string, string>();
    displayGroupedCentres.forEach((country) => {
      country.centres.forEach((centre) => {
        map.set(deriveCentreKey(centre), country.key);
      });
    });
    return map;
  }, [displayGroupedCentres]);

  const countriesOptions = useMemo(() => {
    const isoToName = new Map<string, string>();
    countries.forEach((c: any) => {
      const iso = c?.id != null ? String(c.id).toUpperCase() : "";
      if (iso) isoToName.set(iso, (c.name && c.name.trim()) ? c.name : iso);
    });

    const seen = new Set<string>();
    const opts: { value: string; label: string }[] = [];

    centres.forEach((centre: any) => {
      const numericId = centre?.countryId;
      if (numericId == null) return;

      const value = String(numericId);
      if (seen.has(value)) return;
      seen.add(value);

      const iso = (centre?.centreAddress?.countryISOCode ?? centre?.countryCode ?? "").toUpperCase();
      const label = iso ? (isoToName.get(iso) ?? iso) : value;

      opts.push({ value, label });
    });

    return opts.sort((a, b) => a.label.localeCompare(b.label));
  }, [countries, centres]);

  const onSelectCentre = (centre: Centre) => {
    const code = centre.code ?? (centre as any).id;
    dispatch(selectCentreAndLoad(code) as any);
    dispatch(clearSelectedSchool());
    form.resetFields();
    form.setFieldsValue(defaultFormValues);
    setIsModalOpen(false);
    setCurrentCentre(code);
  };

  const handleCountrySelect = (countryKey: string) => {
    setActiveCountryKey(countryKey);
    setOpenCountryKeys([`country:${countryKey}`]);
    const originalCountry =
      displayGroupedCentres.find((country) => country.key === countryKey) ?? null;

    if (!originalCountry || !originalCountry.centres.length) {
      return;
    }

    const alreadySelected =
      selectedCentreId != null &&
      originalCountry.centres.some(
        (centre) => deriveCentreKey(centre) === String(selectedCentreId)
      );

    if (!alreadySelected) {
      onSelectCentre(originalCountry.centres[0]);
    }
  };

  const handleMenuClick: MenuProps["onClick"] = ({ key }) => {
    if (typeof key !== "string") return;
    if (key.startsWith("country:")) {
      handleCountrySelect(key.slice("country:".length));
      return;
    }
    if (key.startsWith("centre:")) {
      const centreKey = key.slice("centre:".length);
      const countryKey = centreToCountryKey.get(centreKey);
      if (countryKey) {
        setActiveCountryKey(countryKey);
        setOpenCountryKeys([`country:${countryKey}`]);
      }
      const target = centres.find(
        (centre) => deriveCentreKey(centre) === centreKey
      );
      if (target) {
        onSelectCentre(target);
      }
    }
  };

  useEffect(() => {
    if (!filteredCountries.length) {
      if (activeCountryKey !== null) {
        setActiveCountryKey(null);
      }
      return;
    }

    if (selectedCentreId != null) {
      const selectedCountry = filteredCountries.find((country) =>
        country.centres.some(
          (centre) => deriveCentreKey(centre) === String(selectedCentreId)
        )
      );
      if (selectedCountry && selectedCountry.key !== activeCountryKey) {
        setActiveCountryKey(selectedCountry.key);
      }
      return;
    }

    if (activeCountryKey === null) {
      setActiveCountryKey(filteredCountries[0].key);
    }
  }, [filteredCountries, selectedCentreId, activeCountryKey]);

  useEffect(() => {
    if (activeCountryKey) {
      setOpenCountryKeys([`country:${activeCountryKey}`]);
    } else {
      setOpenCountryKeys([]);
    }
  }, [activeCountryKey]);

  const handleOpenChange: MenuProps["onOpenChange"] = (keys) => {
    setOpenCountryKeys(keys as string[]);
  };


  const menuItems = useMemo<MenuProps["items"]>(() => {
    return filteredCountries.map((country) => {
      const count = centreSearch.trim()
        ? country.centres.length
        : country.originalCount;

      return {
        key: `country:${country.key}`,
        label: (
          <span>
            {country.label}{" "}
            <span style={{ color: "#8c8c8c" }}>({count})</span>
          </span>
        ),
        children: country.centres.map((centre) => {
          const centreKey = deriveCentreKey(centre);
          const displayName =
            centre.name ??
            centre.code ??
            (centre as any)?.centreCode ??
            centreKey;
          const isCentreActive = centre.active !== false;

          return {
            key: `centre:${centreKey}`,
            label: (
              <div className="centre-menu-item-label">
                <span
                  className={`centre-status-dot centre-status-dot--${isCentreActive ? "active" : "inactive"}`}
                  aria-hidden
                />
                <span>{displayName}</span>
              </div>
            ),
          };
        }),
      };
    });
  }, [filteredCountries, centreSearch]);

  const onSelectSchoolCard = async (schoolId: string | number | null) => {
    if (schoolId == null) return;
    schoolIdRef.current = null;
    const normalized = normalizeSchoolId(schoolId) ?? schoolId;
    await dispatch(selectSchoolForEdit(normalized) as any);
    setIsModalOpen(true);
  };


  const onAddNewSchool = () => {
    if (!selectedCentreId) {
      message.info("Select a centre first");
      return;
    }
    dispatch(clearSelectedSchool());
    form.resetFields();
    setInitialExcludedBookingSystems([]);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    if (checkFormDirty()) {
      setPendingAction('close-modal');
    } else {
      closeModalAndReset();
    }
  };

  const closeModalAndReset = useCallback(() => {
    schoolIdRef.current = null;
    setIsModalOpen(false);
    form.resetFields();
    setInitialExcludedBookingSystems([]);
    form.setFieldsValue(defaultFormValues);
    setInitialFormValues(null);
    setInitialDecommissionDate(null);
    setPendingAction(null);
    lastOpenedSchoolIdRef.current = null;
    if (formMode !== "edit") {
      dispatch(clearSelectedSchool());
    }
  }, [dispatch, form, formMode]);

  const handlePendingConfirm = useCallback(() => {
    if (pendingAction === 'close-modal') {
      closeModalAndReset();
    }
    setPendingAction(null);
  }, [closeModalAndReset, pendingAction]);

  const handlePendingStay = useCallback(() => {
    setPendingAction(null);
  }, []);

  const onFinish = async (activate:boolean = false) => {
    try {
      const values = await form.validateFields();
      if(activate === true)
        values.isActive = true;
      const selectedSystems = Array.isArray(values.excludedBookingSystemIds)
          ? values.excludedBookingSystemIds.map((id) => String(id))
          : [];

      const result = await dispatch(submitSchoolForm(values) as any);
      if (!result?.ok) {
        message.error(result?.message || "Failed to save school");
        return;
      }

      setIsModalOpen(false);
      setPendingAction(null);
      message.success("School saved successfully");

      const savedId =
          result?.savedId ??
          normalizeSchoolId(values.id ?? selectedSchoolId ?? selectedSchoolDetail?.id ?? null);

      if (savedId != null) {
        (async () => {
          try {
            const updatedIds = await updateBookingSystemSchoolExclusions(
                savedId,
                selectedSystems,
                initialExcludedBookingSystems
            );
            setInitialExcludedBookingSystems(updatedIds);
          } catch (e: any) {
            message.error(e?.message || "Failed to update booking system exclusions.");
          }
        })();
      }

      form.resetFields();
      form.setFieldsValue(defaultFormValues);
    } catch (e: unknown) {
      const firstInvalid = getFirstInvalidFieldName(e);
      if (firstInvalid) {
        focusFirstInvalidField(form, firstInvalid);
      }
    }
  };

  const confirmUpdateAndReactivate = useCallback(() => {
    Modal.confirm({
      title: "Confirm School Reactivation",
      content:
        "Are you sure you want to reactivate the School? It will appear under the Schools list for this Centre.",
      okText: "Reactivate",
      cancelText: "Cancel",
      onOk: async () => {
        await onFinish(true);
      },
    });
  }, [onFinish]);

  const isDetailLoading =
    detailLoadingId != null &&
    selectedSchoolId != null &&
    String(detailLoadingId) === String(selectedSchoolId);

  const centreSelected = selectedCentreId != null && selectedCentreId !== "";

  const visibleSchools = useMemo(() => {
    if (showInactive) return schools;
    return schools.filter((s) => s.isActive !== false);
  }, [schools, showInactive]);

  const phoneRules = schoolPhoneRules;

  const selectedCentre = useMemo(() => {
    if (selectedCentreId == null || selectedCentreId === "") return undefined;
    return findCentreBySelectedId(centres, selectedCentreId);
  }, [centres, selectedCentreId]);

  const selectedCentreInactive = selectedCentre?.active === false;
  const canAddNewSchool = centreSelected && !selectedCentreInactive;

  const selectedCentreLabel = useMemo(() => {
    if (!selectedCentre) {
      return "";
    }
    const baseName =
      selectedCentre.name ??
      String(selectedCentre.code ?? (selectedCentre as any)?.centreCode ?? selectedCentre.id ?? "");
    const rawCode = selectedCentre.countryCode ? String(selectedCentre.countryCode).toUpperCase() : "";
    const countryLabel =
      selectedCentre.countryName ?? (rawCode ? countryNameMap.get(rawCode) ?? rawCode : "");
    if (!baseName) {
      return countryLabel ?? "";
    }
    return countryLabel ? `${baseName} (${countryLabel})` : baseName;
  }, [selectedCentre, countryNameMap]);

  const selectedCentreMenuKey = selectedCentre
    ? `centre:${deriveCentreKey(selectedCentre)}`
    : null;

  const menuSelectedKeys = selectedCentreMenuKey
    ? [selectedCentreMenuKey]
    : activeCountryKey
    ? [`country:${activeCountryKey}`]
    : [];

  const isActive = Form.useWatch("isActive", form);

  const handleDecommissionDateChange = (newValue: Dayjs | null) => {
    if (!newValue) {
      setDecommissionDate(null);
      return;
    }

    const title = selectedSchoolDetail?.lastActiveSchoolInCentre === true ? "Confirm School and Centre Closure Date" : "Confirm Closure Date";
    const content = selectedSchoolDetail?.lastActiveSchoolInCentre === true ? `Saving will automatically inactivate this school and centre on ${newValue.format("YYYY-MM-DD")} as this is the last active School for this Centre. Continue?` : `Saving will automatically inactivate this school on ${newValue.format("YYYY-MM-DD")}. Continue?`;

    Modal.confirm({
      title: title,
      content: content,
      onOk: () => setDecommissionDate(newValue),
      onCancel: () => form.setFieldValue("decommissionDate", decommissionDate)
    });
  };

  return (
    <>
      <Layout style={{ height: "100%", background: "#fff" }}>
        <Sider
          width={260}
          theme="light"
          style={{
            background: "#fff",
            borderRight: "1px solid #f0f0f0",
            padding: "16px",
          }}
        >
          <Title level={5} style={{ marginBottom: 12, marginTop: 0 }}>Centre</Title>
          <Input.Search
            placeholder="Search centres"
            value={centreSearch}
            onChange={(e) => setCentreSearch(e.target.value)}
            allowClear
          />
          <div className="show-inactive-row">
            <span className="show-inactive-label">Show inactive</span>
            <Switch
              size="small"
              checked={showInactive}
              onChange={setShowInactive}
            />
          </div>
          <div style={{ marginTop: 16 }}>
            {centresLoading ? (
              <div style={{ paddingTop: 24 }}>
                <Space orientation="vertical" style={{ width: "100%", alignItems: "center" }}>
                  <Spin />
                  <Text type="secondary">Loading centres...</Text>
                </Space>
              </div>
            ) : !filteredCountries.length ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  centreSearch.trim()
                    ? "No centres match your search"
                    : "No centres found"
                }
                style={{ marginTop: 24 }}
              />
            ) : (
              <>
                <Menu
                  mode="inline"
                  selectedKeys={menuSelectedKeys}
                  openKeys={openCountryKeys}
                  onOpenChange={handleOpenChange}
                  onClick={handleMenuClick}
                  items={menuItems}
                  style={{ borderInlineEnd: 0 }}
                />
              </>
            )}
          </div>
        </Sider>
        <Content style={{ padding: 16, overflow: "auto" }}>
          <Flex align="center" justify="space-between" wrap style={{ gap: 12 }}>
            <Space orientation="vertical" size={0}>
              <Title level={4} style={{ margin: 0 }}>
                School Manager
              </Title>
              <Text type="secondary">
                {centreSelected && selectedCentreLabel
                  ? `Showing schools for ${selectedCentreLabel}`
                  : "Select a centre to view schools"}
              </Text>
            </Space>
            <Space wrap>
              {centreSelected && (
                <StatusIcon
                  status={status}
                  lastEventReceived={lastEventReceived}
                  unpublishedChanges={
                    Array.isArray(schools) && schools.some((x) => x?.entityUpdate === true)
                  }
                  onManualRefresh={refreshGridEvent}
                />
              )}
            </Space>
          </Flex>
          <div style={{ marginTop: 12, borderTop: "1px solid #f0f0f0" }} />

          <Spin spinning={schoolsLoading} description="Loading schools...">
            {!centreSelected ? (
              <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
                <Col span={24}>
                  <Card variant="borderless" style={{ textAlign: "center" }}>
                    <Empty description="Select a centre to view schools" />
                  </Card>
                </Col>
              </Row>
            ) : visibleSchools.length === 0 ? (
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={8} lg={6}>
                    <Tooltip
                      title={
                        selectedCentreInactive
                          ? "This centre is inactive. You can't add a new school."
                          : undefined
                      }
                    >
                      <Card
                        key="add"
                        hoverable={canAddNewSchool}
                        onClick={canAddNewSchool ? onAddNewSchool : undefined}
                        style={{
                          minHeight: 180,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: "1px dashed #d9d9d9",
                          cursor: canAddNewSchool ? "pointer" : "not-allowed",
                          opacity: canAddNewSchool ? 1 : 0.6,
                        }}
                      >
                        <Space orientation="vertical" align="center">
                          <PlusOutlined style={{ fontSize: 24 }} />
                          <Text>+ Add New School</Text>
                        </Space>
                      </Card>
                    </Tooltip>
                  </Col>
                </Row>
              ) : (
                <Row gutter={[16, 16]}>
                  {visibleSchools.map((school) => {
                    const key = school.id ?? school.code;
                    const isSelected = selectedSchoolId != null && String(selectedSchoolId) === String(key);
                    const locked = school.entityUpdate;
                    return (
                      <Col key={String(key)} xs={24} sm={12} md={8}>
                        <Card
                          hoverable
                          onClick={() => locked ? undefined : onSelectSchoolCard(key as any)}
                          style={{
                            minHeight: 180,
                            cursor: locked ? "not-allowed" : "pointer",
                            opacity: locked ? 0.6 : 1,
                            backgroundColor: locked ? "#e0e0e0ff" : "white",
                            borderColor: isSelected ? "#1677ff" : undefined,
                            boxShadow: isSelected ? "0 0 0 2px rgba(22, 119, 255, 0.2)" : undefined,
                          }}
                        >
                          <Space orientation="vertical" size={4} style={{ width: "100%" }}>
                            <Flex align="center" justify="space-between">
                            <Title level={5} style={{ marginBottom: 0 }}>
                              {locked ? (
                                <>
                                  <LockOutlined style={{ color: "#8c8c8c", marginRight: 6 }} />
                                  {school.name || "Unnamed School"} (Publishing an update)
                                </>
                              ) : (
                                school.name || "Unnamed School"
                              )}
                            </Title>
                            <Tag color={school.isActive ? "green" : "red"}>
                              {school.isActive ? school.decommissionDate ? (() => {
                                const decommissionDate = school.decommissionDate;
                                const dateValue = (decommissionDate && typeof (decommissionDate as any).toDate === 'function')
                                  ? (decommissionDate as any).toDate()
                                  : DateHelper.ToDayJS(decommissionDate as unknown as Date | string).toDate();
                                return `Active until ${DateHelper.ToDateString(dateValue)}`;
                              })() : "Active" : "Inactive" }
                            </Tag>
                            </Flex>
                            <Text type="secondary">School Code: {school.code || "-"}</Text>
                            <Text type="secondary">Emergency No: {school.emergencyNo || "-"}</Text>
                          </Space>
                        </Card>
                      </Col>
                    );
                  })}
                  <Col xs={24} sm={12} md={8}>
                    <Tooltip
                      title={
                        selectedCentreInactive
                          ? "This centre is inactive. You can't add a new school."
                          : undefined
                      }
                    >
                      <Card
                        key="add"
                        hoverable={canAddNewSchool}
                        onClick={canAddNewSchool ? onAddNewSchool : undefined}
                        style={{
                          minHeight: 180,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: canAddNewSchool ? "pointer" : "not-allowed",
                          opacity: canAddNewSchool ? 1 : 0.6,
                        }}
                      >
                        <Space orientation="vertical" align="center">
                          <PlusOutlined style={{ fontSize: 24 }} />
                          <Text>Add New School</Text>
                          <Text type="secondary">Create a new School record</Text>
                        </Space>
                      </Card>
                    </Tooltip>
                  </Col>
                </Row>
              )}
          </Spin>
        </Content>
      </Layout>

      <Modal
        title={formMode === "edit" ? "Edit School" : "Create School"}
        open={isModalOpen}
        onCancel={handleModalClose}
        width={"60%"}
        confirmLoading={saving}
        destroyOnHidden={false}
        mask={{ closable: false }}
        forceRender
        footer={[
          <Button key="btnCancel" onClick={handleModalClose}>Cancel</Button>,
          <Button key="btnUpdateSave" onClick={() => onFinish()} type="primary" disabled={!!selectedSchoolDetail?.entityUpdate}>
            {formMode === "edit" ? "Update" : "Save"}
          </Button>,
          ...(formMode === "edit" && selectedSchoolDetail?.isActive === false
            ? [
                <Button
                  key="btnUpdateAndReactivate"
                  onClick={confirmUpdateAndReactivate}
                  type="primary"
                  disabled={saving || !!selectedSchoolDetail?.entityUpdate}
                >
                  Update and Reactivate
                </Button>,
              ]
            : []),
        ]}
      >
        <Spin spinning={saving || isDetailLoading} description={saving ? "Saving..." : "Loading details..."}>
          <Form
            form={form}
            layout="vertical"
            initialValues={defaultFormValues}
            disabled={!centreSelected || !!selectedSchoolDetail?.entityUpdate}
          >
             <Form.Item name="id" hidden>
                <Input />
              </Form.Item>
            {selectedSchoolDetail?.entityUpdate && (
              <Alert type="info" title="Publishing an update" description="This school is locked while an update is being published. Editing is disabled." showIcon style={{ marginBottom: 16 }} />
            )}
            {saveError && (
              <Alert type="error" title={saveError} showIcon style={{ marginBottom: 16 }} />
            )}
            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="School Name"
                  name="schoolName"
                  rules={[
                    { required: true, message: "Enter the school name" },
                    entityNameNoSpecialCharsRule,
                    entityNameMaxLengthRule,
                  ]}
                >
                  <Input placeholder="Enter school name" allowClear maxLength={50} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="School Code"
                  name="schoolCode"
                  rules={[
                    { required: true, message: "Enter the school code" },
                    { max: 6, message: "School code cannot exceed 6 characters." },
                  ]}
                >
                  <Input placeholder="Enter school code" allowClear maxLength={6} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Form.Item label="Telephone No" name="telephoneNo" rules={phoneRules}>
                  <Input placeholder="e.g. +44 20 1234 5678" allowClear maxLength={20} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Emergency No" name="emergencyNo" rules={phoneRules}>
                  <Input placeholder="e.g. +44 20 9876 5432" allowClear maxLength={20} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col xs={24} md={12} lg={6}>
                <Form.Item
                  label="Age From"
                  name="ageFrom"
                  rules={[{ required: true, message: "Enter minimum age" }]}
                >
                  <InputNumber min={0} max={120} style={{ width: "100%" }} placeholder="Minimum age" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={6}>
                <Form.Item
                  label="Age To"
                  name="ageTo"
                  rules={[
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (value == null || value === "") return Promise.resolve();
                          const to = Number(value);
                          if (Number.isNaN(to)) return Promise.reject(new Error("Enter a valid number"));
                          const from = Number(getFieldValue("ageFrom"));
                          if (Number.isNaN(from)) return Promise.reject(new Error("Enter Age From first"));
                          if (to < from) return Promise.reject(new Error("Age To must be greater than or equal to Age From"));
                          return Promise.resolve();
                        },
                      }),
                    ]}
                >
                  <InputNumber min={0} max={120} style={{ width: "100%" }} placeholder="Optional" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={6}>
                <Form.Item hidden name="isActive">
                  <Input/>
                </Form.Item>
                <Form.Item label="LMS Access" name="lmsAccess" valuePropName="checked">
                  <Checkbox>LMS Access Enabled</Checkbox>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Form.Item
                  label={
                    <span>
                      School Open on Weekends&nbsp;
                      <Tooltip title="Choose whether the school is open on Saturday and Sunday"><InfoCircleOutlined /></Tooltip>
                    </span>
                  }
                >
                  <Space size="large">
                    <Form.Item name="opensOnSaturday" valuePropName="checked" noStyle>
                      <Checkbox>Saturday</Checkbox>
                    </Form.Item>
                    <Form.Item name="opensOnSunday" valuePropName="checked" noStyle>
                      <Checkbox>Sunday</Checkbox>
                    </Form.Item>
                  </Space>
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={6}>
                <Form.Item label={
                  <span>
                    Closure Date&nbsp;
                    <Tooltip title="On this date, the school will be automatically inactivated and all products decommissioned."><InfoCircleOutlined /></Tooltip>
                  </span>
                } name="decommissionDate">
                    <DatePicker style={{ width: '100%' }} format={DateHelper.DATE_PICKER_FORMATS} onChange={handleDecommissionDateChange} disabled={!isActive} disabledDate={(current) => current && current < dayjs().endOf('day') }/>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Form.Item label="Building" name="addressBuilding" rules={[{ max: 150, message: "Building must be less or equal 150 characters" }]}>
                  <Input placeholder="Building / Apartment" allowClear maxLength={150} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Street" name="addressStreet" rules={[{ max: 100, message: "Street must be less or equal 100 characters" }]}>
                  <Input placeholder="Street" allowClear maxLength={100} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Form.Item label="City" name="addressCity" rules={[{ max: 50, message: "City must be less or equal 50 characters" }]}>
                  <Input placeholder="City" allowClear maxLength={50} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Postal Code" name="addressPostalCode" rules={[{ max: 100, message: "Postal Code must be less or equal 100 characters" }]}>
                  <Input placeholder="Postal Code" allowClear maxLength={100} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Form.Item label="Country" name="addressCountry">
                  <Select
                    loading={countriesLoading}
                    options={countriesOptions}
                    showSearch={{ optionFilterProp: 'label' }}
                    placeholder={countriesOptions.length ? "Select country" : "No countries available"}
                    disabled={!countriesOptions.length}
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label={
                <>
                  <span style={{ fontWeight: 'bold', color: 'red', marginRight: 3 }}>Exclude</span>
                  {' this school from the following booking systems:'}
                </>
              }
              name="excludedBookingSystemIds"
            >
              <Checkbox.Group
                style={{ display: "flex", flexDirection: "column", gap: 8 }}
                disabled={bookingSystemsLoading}
              >
                {bookingSystems.map((system) => (
                  <Checkbox key={system.id} value={String(system.id)}>
                    {system.name}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            </Form.Item>

            {detailError && (
              <Alert type="error" title={detailError} showIcon style={{ marginBottom: 16 }} />
            )}
          </Form>
        </Spin>
      </Modal>
      <UnsavedChangesModal
        open={pendingAction !== null}
        onLeave={handlePendingConfirm}
        onStay={handlePendingStay}
      />
    </>
  );
};

export default Page;